import { Component, Input, OnInit } from '@angular/core';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { first } from 'rxjs/operators';
import { ISession } from '../../../models/session';
import { ISegment, ISegmentUser, SegmentsService } from '../../sessions/segments.service';
import { ConfirmationModalComponent } from '../../modals/confirmation/confirmation-modal.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { UsersService } from '../../sessions/users/users.service';
import { IRegistrationField } from '../../../models/registrationField';

@Component({
  selector: 'email-segments',
  templateUrl: './segments.component.html',
  styleUrls: ['./segments.component.scss']
})
export class SegmentsComponent implements OnInit {
  @Input() public session: ISession;

  public segments: ISegment[] = [];
  public isLoading = false;
  public registrationFields: IRegistrationField[] = [];

  // Pagination
  public currentPage = 1;
  public readonly pageSize = 10;

  // Sorting
  public sortBy = 'created_at';
  public sortOrder: 'asc' | 'desc' = 'desc';

  // Segment modal
  public showSegmentModal = false;
  public editingSegment: ISegment | null = null;
  public editingSegmentId: string | null = null;
  public isSavingSegment = false;

  // Preview
  public showPreviewModal = false;
  public previewSegmentName = '';
  public previewUsers: ISegmentUser[] = [];
  public previewTotal = 0;
  public previewPage = 0;
  public readonly previewPageSize = 10;
  public isLoadingPreview = false;

  constructor(
    private usersService: UsersService,
    private segmentsService: SegmentsService,
    private translateService: TranslateService,
    private modalService: BsModalService,
  ) {}

  ngOnInit() {
    this.loadSegments();
    this.loadRegistrationForm();
  }

  get totalPages(): number {
    return Math.ceil(this.segments.length / this.pageSize);
  }

  get sortedSegments(): ISegment[] {
    const dir = this.sortOrder === 'asc' ? 1 : -1;
    return [...this.segments].sort((a, b) => {
      const av = (a as any)[this.sortBy] || '';
      const bv = (b as any)[this.sortBy] || '';
      return av < bv ? -dir : av > bv ? dir : 0;
    });
  }

  get pagedSegments(): ISegment[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.sortedSegments.slice(start, start + this.pageSize);
  }

  public sort(column: string) {
    if (this.sortBy === column) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = column;
      this.sortOrder = 'asc';
    }
    this.currentPage = 1;
  }

  public getSortIcon(column: string): string {
    if (this.sortBy !== column) return 'fa-sort';
    return this.sortOrder === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc';
  }

  public goToPage(page: number) {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  public formatDate(date: string): string {
    if (!date) return '-';
    const lang = this.translateService.store.currentLang || this.translateService.store.defaultLang;
    return lang.toLowerCase().substring(0, 2) === 'fr' ? moment(date).format('DD/MM/YYYY HH:mm') : moment(date).format('MM/DD/YYYY, hh:mm A');
  }

  public getPages(): number[] {
    const pages = [];
    for (let i = 1; i <= this.totalPages; i++) pages.push(i);
    return pages;
  }

  public loadRegistrationForm() {
    this.usersService.getRegistration(this.session).subscribe(
      (fields: any[]) => {
        this.registrationFields = fields || [];
      },
      _err => toastr.error(this.translateService.instant('action.failed'))
    );
  }

  public loadSegments() {
    this.isLoading = true;
    this.segmentsService.getSegments(this.session)
      .subscribe(
        (segments: ISegment[]) => {
          this.segments = segments || [];
          this.currentPage = 1;
          this.isLoading = false;
        },
        _err => {
          toastr.error(this.translateService.instant('action.failed'));
          this.isLoading = false;
        }
      );
  }

  // Segment modal

  public openCreateSegmentModal() {
    this.editingSegmentId = null;
    this.editingSegment = { name: '', search_criteria: { filters: [] } };
    this.showSegmentModal = true;
  }

  public closeSegmentModal() {
    this.showSegmentModal = false;
    this.editingSegment = null;
    this.editingSegmentId = null;
  }

  public openEditSegmentModal(segment: ISegment) {
    this.editingSegmentId = segment.segment_id || null;
    this.editingSegment = {
      ...segment,
      search_criteria: { filters: segment.search_criteria ? [...segment.search_criteria.filters] : [] }
    };
    this.showSegmentModal = true;
  }

  public onSegmentChange(segment: ISegment) {
    this.editingSegment = segment;
  }

  public openPreviewModal(segment: ISegment) {
    this.editingSegmentId = segment.segment_id || null;
    this.previewSegmentName = segment.name;
    this.previewPage = 0;
    this.previewUsers = [];
    this.previewTotal = 0;
    this.showPreviewModal = true;
    this.loadPreview();
  }

  public closePreviewModal() {
    this.showPreviewModal = false;
    this.editingSegmentId = null;
  }

  public loadPreview() {
    if (!this.editingSegmentId) return;
    this.isLoadingPreview = true;
    this.segmentsService.previewSegment(this.session, this.editingSegmentId, this.previewPageSize, this.previewPage * this.previewPageSize)
      .subscribe(
        result => {
          this.previewUsers = result.users || [];
          this.previewTotal = result.users_count;
          this.isLoadingPreview = false;
        },
        _err => {
          toastr.error(this.translateService.instant('action.failed'));
          this.isLoadingPreview = false;
        }
      );
  }

  public previewGoToPage(page: number) {
    this.previewPage = page;
    this.loadPreview();
  }

  get previewTotalPages(): number {
    return Math.ceil(this.previewTotal / this.previewPageSize);
  }

  public saveSegment() {
    if (!this.editingSegment || !this.editingSegment.name || !this.editingSegment.search_criteria || this.editingSegment.search_criteria.filters.length === 0) return;
    this.isSavingSegment = true;

    const dto: ISegment = {
      segment_id: this.editingSegmentId || undefined,
      name: this.editingSegment.name,
      search_criteria: { filters: this.editingSegment.search_criteria.filters }
    };

    const request$ = this.editingSegmentId
      ? this.segmentsService.updateSegment(this.session, dto)
      : this.segmentsService.createSegment(this.session, dto);

    request$.subscribe(
      () => {
        this.isSavingSegment = false;
        this.closeSegmentModal();
        this.loadSegments();
      },
      _err => {
        this.isSavingSegment = false;
        toastr.error(this.translateService.instant('action.failed'));
      }
    );
  }

  public deleteSegment(segment: ISegment) {
    const modal = this.modalService.show(ConfirmationModalComponent);
    (modal.content as ConfirmationModalComponent).showConfirmationModal(
      this.translateService.instant('sessions.segments.delete_confirm')
    );
    (modal.content as ConfirmationModalComponent).onClose
      .pipe(first())
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.segmentsService.deleteSegment(this.session, segment.segment_id)
          .subscribe(
            () => {
              if (this.pagedSegments.length === 1 && this.currentPage > 1) this.currentPage--;
              this.loadSegments();
            },
            _err => toastr.error(this.translateService.instant('action.failed'))
          );
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/email/segments/segments.component.ts